/**
 * `useReportAuthoringChat` — client half of the /reports/new streaming
 * report-builder agent.
 *
 * Wraps `useChat` (AI SDK) against `POST /api/ai/reports/chat` — the
 * same transport Ask Flint uses — and layers on the panel state this
 * surface exists for:
 *
 *   - `currentDraft` — what the right-side preview panel shows. Updated
 *     live mid-stream from transient `data-report-draft` parts (one per
 *     successful `propose_report` tool call).
 *   - `savedKey` — set when the agent saves via `save_report` /
 *     `update_report` (transient `data-report-saved` part) OR when the
 *     user clicks the manual save button (`saveMutation`). Unlocks the
 *     preview pane's SQL / Template edit tabs.
 *
 * The chat is EPHEMERAL by design — nothing persists server-side;
 * "New chat" is a local remount (the page bumps the component key).
 * Each turn sends `activeDraft` in the request body: message history
 * can't carry the panel state (the server strips `data-*` parts before
 * the model sees them), so the client owns it and sends the branch
 * point explicitly.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import type { reports as reportsContract } from "@flint/api-contract";
import { toast } from "@flint/ui";
import { WEB_ENV } from "../../lib/env.ts";
import { trpc } from "../../lib/trpc.ts";

type DraftDefinition = reportsContract.DraftDefinition;

export interface UseReportAuthoringChatOptions {
  /**
   * Edit-mode: bootstraps `currentDraft` from the saved report (via
   * `getDefinition` + `previewAutoTemplate`) and routes agent saves
   * through `update_report`. The SQL/Template tabs are editable from
   * the first render.
   */
  editReportKey?: string;
}

export function useReportAuthoringChat(options: UseReportAuthoringChatOptions = {}) {
  const editReportKey = options.editReportKey;

  const [currentDraft, setCurrentDraft] = useState<DraftDefinition | null>(null);
  /**
   * Server-rendered HTML when the draft carries a custom
   * `displayTemplate` (KPI cards, sections…). Null = plain-table draft;
   * the panel falls back to the client-side flat table.
   */
  const [currentHtml, setCurrentHtml] = useState<string | null>(null);
  const [agentSavedKey, setAgentSavedKey] = useState<string | undefined>(undefined);
  // Ref mirror so `send` never closes over a stale draft (the user can
  // send immediately after a draft lands without a re-render between).
  const draftRef = useRef<DraftDefinition | null>(null);
  useEffect(() => {
    draftRef.current = currentDraft;
  }, [currentDraft]);

  const utils = trpc.useUtils();

  const transport = useMemo(
    () =>
      new DefaultChatTransport<UIMessage>({
        api: `${WEB_ENV.VITE_FLINT_API_URL}/api/ai/reports/chat`,
        credentials: "include",
      }),
    [],
  );

  const { messages, sendMessage, stop, status, error } = useChat<UIMessage>({
    transport,
    onData: (part) => {
      if (isReportDraftPart(part)) {
        setCurrentDraft(part.data.draft);
        setCurrentHtml(part.data.html ?? null);
      } else if (isReportSavedPart(part)) {
        setAgentSavedKey(part.data.reportKey);
        void utils.reports.listDefinitions.invalidate();
        if (editReportKey) {
          void utils.reports.getDefinition.invalidate({ key: part.data.reportKey });
          toast.success("Report updated");
        } else {
          toast.success(`Saved as ${part.data.reportKey}`);
        }
      }
    },
  });

  const send = useCallback(
    (raw: string) => {
      const text = raw.trim();
      if (!text) return;
      void sendMessage(
        { text },
        {
          body: {
            ...(draftRef.current ? { activeDraft: draftRef.current } : {}),
            ...(editReportKey ? { editReportKey } : {}),
          },
        },
      );
    },
    [sendMessage, editReportKey],
  );

  // ── Save flow (footer button) ─────────────────────────────────────
  // First save of a fresh draft. Successful save populates `agentSavedKey`
  // so the SQL / Template tabs unlock and the footer switches from
  // first-save to update mode without a page refresh.
  const saveMutation = trpc.reports.saveAiDraft.useMutation({
    onSuccess: (data) => {
      setAgentSavedKey(data.reportKey);
      void utils.reports.listDefinitions.invalidate();
      toast.success(`Saved as ${data.reportKey}`);
    },
  });
  const updateMutation = trpc.reports.updateAiDraft.useMutation({
    onSuccess: (_data, variables) => {
      void utils.reports.getDefinition.invalidate({ key: variables.reportKey });
      toast.success("Saved");
    },
  });
  const updateTemplateMutation = trpc.reports.updateAiDraftTemplate.useMutation({
    onSuccess: (_data, variables) => {
      void utils.reports.getDefinition.invalidate({ key: variables.reportKey });
      toast.success("Saved");
    },
  });

  // ── Edit-mode bootstrap ───────────────────────────────────────────
  // Fetch the saved definition, run its SQL through the ad-hoc executor
  // (via previewAutoTemplate) for real rows, and seed `currentDraft` so
  // the panel renders before the first chat turn.
  const editQuery = trpc.reports.getDefinition.useQuery(
    { key: editReportKey ?? "" },
    { enabled: !!editReportKey, staleTime: Infinity, refetchOnWindowFocus: false },
  );
  const editPreviewQuery = trpc.reports.previewAutoTemplate.useQuery(
    {
      sqlTemplate: editQuery.data?.sqlTemplate ?? "",
      labels: [],
      reportName: editQuery.data?.name ?? "",
    },
    {
      enabled: !!editReportKey && !!editQuery.data,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  );
  const hasEditBootstrapped = useRef(false);
  useEffect(() => {
    if (!editReportKey) return;
    if (hasEditBootstrapped.current) return;
    if (!editQuery.data) return;
    // Wait for the preview too so the Preview tab has real rows on
    // first render. If it errored (SQL no longer valid), fall back to
    // an empty preview — the SQL tab still works for fixing it.
    if (editPreviewQuery.isLoading) return;
    hasEditBootstrapped.current = true;
    const def = editQuery.data;
    const preview = editPreviewQuery.data;
    setCurrentDraft({
      title: def.name,
      subtitle: def.description ?? "",
      description: [],
      // Labels aren't persisted structurally — the SQL aliases from the
      // ad-hoc executor are the fallback (same source updateAiDraft
      // uses when regenerating templates).
      columns: preview?.columns ?? [],
      placeholderRows: preview?.rows ?? [],
      sqlTemplate: def.sqlTemplate,
      // Always hydrate — the SQL tab's stepper needs the real persisted
      // shape (step keys, names, order). Fresh AI drafts leave this
      // undefined and the stepper derives one implicit step from
      // `sqlTemplate`.
      dataSources: def.dataSources,
    });
  }, [editReportKey, editQuery.data, editPreviewQuery.isLoading, editPreviewQuery.data]);

  const isBusy = status === "submitted" || status === "streaming";
  // Show the "working" indicator throughout the entire no-text window:
  // before the first assistant part, while a reasoning part is streaming
  // (including [REDACTED]), and while tool calls are firing. It hides only
  // once the assistant has produced visible prose — matching the Taqat
  // "TeamMate is working on your request…" persistence.
  const isWaitingForAssistant = isBusy && !hasVisibleAssistantText(messages);

  return {
    messages,
    send,
    stop,
    status,
    error,
    isBusy,
    isWaitingForAssistant,
    currentDraft,
    currentHtml,
    /** Edit-mode key wins (URL is truth); otherwise the manual save. */
    savedKey: editReportKey ?? agentSavedKey,
    isEditHydrating: !!editReportKey && !currentDraft,
    saveMutation,
    updateMutation,
    updateTemplateMutation,
  };
}

function hasVisibleAssistantText(messages: UIMessage[]): boolean {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return false;
  return last.parts.some(
    (part) =>
      part.type === "text" &&
      typeof (part as { text?: unknown }).text === "string" &&
      ((part as { text: string }).text.trim().length > 0),
  );
}

function isReportDraftPart(
  part: unknown,
): part is { type: "data-report-draft"; data: { draft: DraftDefinition; html?: string } } {
  return (
    typeof part === "object" &&
    part !== null &&
    "type" in part &&
    (part as { type: unknown }).type === "data-report-draft" &&
    "data" in part &&
    typeof (part as { data: unknown }).data === "object"
  );
}

function isReportSavedPart(
  part: unknown,
): part is { type: "data-report-saved"; data: { reportKey: string } } {
  return (
    typeof part === "object" &&
    part !== null &&
    "type" in part &&
    (part as { type: unknown }).type === "data-report-saved" &&
    "data" in part &&
    typeof (part as { data: { reportKey?: unknown } }).data?.reportKey === "string"
  );
}
